import { cn } from "./utils/cn.js";

/*
 * Wise Owl icon set — 19 custom line icons.
 *
 * Direct port of the `I.*` helper in the brand handoff bundle's
 * app/shared.jsx. All icons:
 *   - 24x24 viewBox
 *   - 1.6–1.8 stroke
 *   - `currentColor` stroke (no fills)
 *   - `strokeLinecap="round"` and `strokeLinejoin="round"`
 *
 * Per the brand bible: no emoji, no icon font. If a needed icon is
 * missing, add it here in the same style — do not import from a
 * third-party set.
 */

export type IconName =
  | "arrow"
  | "back"
  | "close"
  | "plus"
  | "send"
  | "mic"
  | "home"
  | "book"
  | "chat"
  | "chart"
  | "user"
  | "flame"
  | "spark"
  | "leaf"
  | "bookmark"
  | "history"
  | "bell"
  | "search"
  | "settings";

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  /** Accessible label. When omitted, the icon is decorative (`aria-hidden`). */
  label?: string;
}

interface PathConfig {
  d: string;
  w?: number; // stroke-width override
  linejoin?: boolean;
}

interface IconConfig {
  paths: PathConfig[];
  circles?: { cx: number; cy: number; r: number; w?: number }[];
  rects?: { x: number; y: number; w: number; h: number; rx?: number; sw?: number }[];
}

const ICONS: Record<IconName, IconConfig> = {
  arrow: {
    paths: [{ d: "M5 12h14M13 6l6 6-6 6", w: 1.8 }],
  },
  back: {
    paths: [{ d: "M15 6l-6 6 6 6", w: 1.8 }],
  },
  close: {
    paths: [{ d: "M6 6l12 12M18 6L6 18", w: 1.8 }],
  },
  plus: {
    paths: [{ d: "M12 5v14M5 12h14", w: 1.8 }],
  },
  send: {
    paths: [{ d: "M4 12l16-8-6 16-3-6-7-2z", w: 1.6, linejoin: true }],
  },
  mic: {
    paths: [{ d: "M5 11a7 7 0 0 0 14 0M12 18v3", w: 1.6 }],
    rects: [{ x: 9, y: 3, w: 6, h: 12, rx: 3, sw: 1.6 }],
  },
  home: {
    paths: [{ d: "M4 11l8-7 8 7v9h-5v-6h-6v6H4v-9z", w: 1.7, linejoin: true }],
  },
  book: {
    paths: [
      { d: "M5 4h10a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4z", w: 1.7, linejoin: true },
      { d: "M5 17a3 3 0 0 1 3-3h10", w: 1.7 },
    ],
  },
  chat: {
    paths: [{ d: "M4 5h16v11H8l-4 4V5z", w: 1.7, linejoin: true }],
  },
  chart: {
    paths: [{ d: "M4 19V5M4 19h16M8 15v-4M12 15V8M16 15v-6", w: 1.7 }],
  },
  user: {
    paths: [{ d: "M4 21c1-4 4-6 8-6s7 2 8 6", w: 1.7 }],
    circles: [{ cx: 12, cy: 8, r: 4, w: 1.7 }],
  },
  flame: {
    paths: [
      { d: "M12 3s5 5 5 10a5 5 0 1 1-10 0c0-2 1-3 1-3s.5 1.5 1.5 1.5C9.5 11 8 9 9 6c1 1 1.5 2.5 3 3 0-2-1-4 0-6z", w: 1.5, linejoin: true },
    ],
  },
  spark: {
    paths: [{ d: "M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18", w: 1.6 }],
  },
  leaf: {
    paths: [
      { d: "M20 4c0 9-6 15-15 15 0-9 6-15 15-15z", w: 1.6, linejoin: true },
      { d: "M5 19c4-4 7-7 12-12", w: 1.6 },
    ],
  },
  bookmark: {
    paths: [{ d: "M6 4h12v17l-6-4-6 4V4z", w: 1.7, linejoin: true }],
  },
  history: {
    paths: [{ d: "M12 8v4l3 2M5 6L3 4", w: 1.7 }],
    circles: [{ cx: 12, cy: 12, r: 8, w: 1.7 }],
  },
  bell: {
    paths: [{ d: "M6 15V11a6 6 0 0 1 12 0v4l2 3H4l2-3zM10 20a2 2 0 0 0 4 0", w: 1.6, linejoin: true }],
  },
  search: {
    paths: [{ d: "M16 16l4 4", w: 1.7 }],
    circles: [{ cx: 11, cy: 11, r: 6, w: 1.7 }],
  },
  settings: {
    paths: [{ d: "M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.3.9a7 7 0 0 0-1.8-1L14.5 3h-4l-.3 2.4a7 7 0 0 0-1.8 1L6.1 5.6l-2 3.4 2 1.5a7 7 0 0 0 0 2L4.1 14l2 3.4 2.3-.9a7 7 0 0 0 1.8 1L10.5 21h4l.3-2.4a7 7 0 0 0 1.8-1l2.3.9 2-3.4-2-1.5c.1-.4.1-.7.1-1.6z", w: 1.4, linejoin: true }],
    circles: [{ cx: 12, cy: 12, r: 3, w: 1.7 }],
  },
};

export const Icon = ({ name, size = 20, className, label }: IconProps) => {
  const cfg = ICONS[name];
  const ariaProps = label
    ? { role: "img" as const, "aria-label": label }
    : { "aria-hidden": true };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={cn("shrink-0", className)}
      {...ariaProps}
    >
      {cfg.paths.map((p, i) => (
        <path
          key={i}
          d={p.d}
          stroke="currentColor"
          strokeWidth={p.w ?? 1.7}
          strokeLinecap="round"
          strokeLinejoin={p.linejoin ? "round" : undefined}
        />
      ))}
      {cfg.circles?.map((c, i) => (
        <circle
          key={`c${i}`}
          cx={c.cx}
          cy={c.cy}
          r={c.r}
          stroke="currentColor"
          strokeWidth={c.w ?? 1.7}
        />
      ))}
      {cfg.rects?.map((r, i) => (
        <rect
          key={`r${i}`}
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.h}
          rx={r.rx}
          stroke="currentColor"
          strokeWidth={r.sw ?? 1.7}
        />
      ))}
    </svg>
  );
};
